import { useLogGoodsAnalyticsEvent } from './useLogGoodsAnalyticsEvent';
import { useCallback, useEffect, useState } from 'react';
import { bridge, isNil, isNotNil, localStorageBridge } from 'shared/src/utils';
import { useGoodsDetail } from '../queries/useGoodsDetail';
import { useGoodsBasic } from '../queries/useGoodsBasic';
import { useGoodsDiscounts } from '../queries/useGoodsDiscounts';
import { useGoodsCoupon } from '../queries/useGoodsCoupon';
import { useGoodsReviewSummary } from '../queries/useGoodsReviewSummary';
import { EvaluationText } from 'shared/src/constant/domain/review/EvaluationText';
import { useGoodsRoute } from '../../../hooks/useGoodsRoute';

export const useLogViewGoodsDetail = () => {
  const logGoodsAnalytics = useLogGoodsAnalyticsEvent();
  const [isLogged, setIsLogged] = useState(false);
  const { params } = useGoodsRoute<'GoodsDetail'>();
  const { data: goodsDetail } = useGoodsDetail();
  const { data: goodsBasic } = useGoodsBasic();
  const { data: goodsDiscounts, isSuccess: successGoodsDiscounts } =
    useGoodsDiscounts();
  const { data: goodsCoupon, isSuccess: successGoodsCoupon } = useGoodsCoupon();
  const { data: goodsReviewSummary, isSuccess: successGoodReviewSummary } =
    useGoodsReviewSummary();

  const logViewGoodsDetail = useCallback(async () => {
    if (isNil(goodsDetail)) return;
    if (isNil(goodsBasic)) return;

    if (!successGoodsDiscounts) return;
    if (!successGoodsCoupon) return;
    if (!successGoodReviewSummary) return;

    if (isLogged) return;

    const categories = goodsBasic.display_categories?.[0] ?? null;
    const isExpandMaxDiscount =
      (await localStorageBridge.getBoolean(
        'IS_EXPANDED_MAX_DISCOUNT_IN_GOODS_DETAIL',
      )) ?? true;
    const getLoggingAnalytics = () => {
      if (typeof params.extras === 'string') {
        return JSON.parse(params.extras).logging_analytics;
      }

      return params.extras?.logging_analytics
        ? JSON.parse(params.extras.logging_analytics)
        : undefined;
    };

    const result = logGoodsAnalytics({
      name: 'VIEW_GOODS_DETAIL',
      analytics: {
        MARKET_TYPE_SNO: goodsBasic.market.market_vertical_type.sno.toString(),
        PARENT_CATEGORY_SNO: categories?.parent_category.sno.toString(),
        PARENT_CATEGORY_NAME: categories?.parent_category.name,
        IS_EXIST_USABLE_COUPONS: goodsCoupon.is_exist_usable_coupons
          ? '1'
          : '0',
        IS_EXIST_DOWNLOADABLE_COUPONS: isNotNil(goodsCoupon.coupon) ? '1' : '0',
        EXPECTED_DELIVERY_DATE_PROBABILITY:
          goodsDetail.goods.send_expectation_info?.percentage,
        SALE_TYPE: goodsBasic.sale_type,
        IS_EXIST_SIZE_TAB:
          goodsDetail.tabs.filter(tab => tab.tab_type === 'SIZE').length > 0
            ? '1'
            : '0',
        IS_SELLER_DELIVERY_LEADTIME:
          goodsDetail.goods.send_expectation_info?.is_market_provided ?? false,
        IS_EXIST_DELAY_CONTENTS: goodsBasic.is_delayed ?? false,
        REVIEW_SUMMARY_SNOS: goodsReviewSummary.review_summary.map(
          review => review.sno,
        ),
        NUDGING_DESCRIPTION: goodsBasic.price_description?.text,
        IS_TODAY_FINISHED_COUPON: isNotNil(
          goodsCoupon.coupon_cta_info?.cta_description,
        )
          ? '1'
          : '0',
        FIRST_REVIEW_SUMMARY_EVALUATION: goodsReviewSummary.review_summary?.[0]
          ?.eval
          ? EvaluationText[goodsReviewSummary.review_summary[0].eval]
          : undefined,
        IS_OPENED_DISCOUNTED_TOGGLE: isExpandMaxDiscount ? '1' : '0',
        RESTORE: '0',
        ...getLoggingAnalytics(),
        TROPHY: goodsDetail.goods.market.trophies?.[0]?.title,
        DELIVERY_LEAD_TIME:
          goodsDetail.goods.send_expectation_info?.days ?? undefined,
        HAS_STACKABLE_COUPON:
          goodsDiscounts.discount_groups.filter(
            group => group.display_type === 'ISSUABLE',
          ).length > 1
            ? '1'
            : '0',
      },
    });

    if (result) {
      bridge.logAnalyticsEvent({
        name: 'airbridge.ecommerce.product.viewed',
        channel: ['airbridge'],
        analytics: {
          ACTION: goodsBasic.market.market_vertical_type.name,
          LABEL: categories?.name,
          VALUE: goodsBasic.price_info.thumbnail_price,
          CURRENCY: 'KRW',
          PRODUCTS: [
            {
              PRODUCT_ID: goodsBasic.sno.toString(),
              PRODUCT_NAME: goodsBasic.name,
              PRODUCT_CURRENCY: 'KRW',
              PRODUCT_PRICE: goodsBasic.price_info.thumbnail_price,
              PRODUCT_BRAND_ID: goodsBasic.market.sno.toString(),
              PRODUCT_BRAND_NAME: goodsBasic.market.name,
            },
          ],
        },
      });

      bridge.logAnalyticsEvent({
        name: 'fb_mobile_content_view',
        channel: ['facebook'],
        analytics: {
          fb_content_type: 'product',
          fb_content_id: `["${goodsBasic.sno}"]`,
          fb_currency: 'KRW',
          _valueToSum: goodsBasic.price_info.thumbnail_price,
        },
      });
    }

    setIsLogged(result);
  }, [
    goodsBasic,
    goodsCoupon,
    goodsDetail,
    goodsDiscounts,
    goodsReviewSummary,
    isLogged,
    logGoodsAnalytics,
    params.extras,
    successGoodReviewSummary,
    successGoodsCoupon,
    successGoodsDiscounts,
  ]);

  useEffect(() => {
    logViewGoodsDetail();
  }, [logViewGoodsDetail]);
};
